import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { API_ENDPOINTS } from '@shared/constants';
import {
  EVideoRequestStatus,
  IVideo,
  IVideoLink,
  IVideoFile,
  IVideoPayload,
  IVideoRequest,
  IVideoRequestPayload,
} from '@feature/customer/requests/video-requests/models';
import { BaseResponse } from '@core/interfaces';
import { map } from 'rxjs/operators';
import { take, tap } from 'rxjs';

export interface StartTranscodeRequest {
  videoRequestId: number;
  videoPath: string;
  fileName: string;
  fileExtension: string;
}

export interface StartTranscodeResponse {
  jobName: string;
  transcodingStarted: boolean;
  skipReason?: string;
}

export interface TranscodeStatusResponse {
  state: string; // "PROCESSING" | "SUCCEEDED" | "FAILED" | "RETRYING"
  outputFilePath?: string;
  errorMessage?: string;
}

@Injectable({
  providedIn: 'root',
})
export class VideoRequestService {
  readonly #http = inject(HttpClient);

  getVideoRequest(requestId: number) {
    return this.#http
      .get<
        BaseResponse<IVideoRequest>
      >(`${API_ENDPOINTS.VideoRequests.GetById}/${requestId}`)
      .pipe(take(1));
  }

  createVideoRequest(request: IVideoRequestPayload) {
    return this.#http
      .post<
        BaseResponse<unknown>
      >(`${API_ENDPOINTS.VideoRequests.AddVideoRequest}`, request)
      .pipe(take(1));
  }

  updateVideoRequest(request: IVideoRequestPayload) {
    return this.#http
      .put<
        BaseResponse<unknown>
      >(`${API_ENDPOINTS.VideoRequests.UpdateVideoRequest}`, request)
      .pipe(take(1));
  }

  updateStatus(
    requestIds: number | number[],
    status: EVideoRequestStatus,
    type = 'Status'
  ) {
    return this.#http
      .patch<
        BaseResponse<unknown>
      >(`${API_ENDPOINTS.VideoRequests.UpdateStatus}/${type}/${status}`, Array.isArray(requestIds) ? requestIds : [requestIds])
      .pipe(take(1));
  }

  getRequestVideos(requestId: number) {
    return this.#http
      .get<
        BaseResponse<IVideo>
      >(`${API_ENDPOINTS.VideoRequests.GetRequestVideos}/${requestId}`)
      .pipe(
        take(1),
        map(response => {
          if (response.status === 200) {
            return response.data;
          }
          return [];
        })
      );
  }

  saveUploadedVideo(video: IVideoPayload) {
    return this.#http
      .post<
        BaseResponse<{ result: number }>
      >(API_ENDPOINTS.VideoRequests.SaveVideo, video)
      .pipe(take(1));
  }

  updateVideoLinks(requestId: number, videoLinks: IVideoLink[]) {
    return this.#http
      .put<BaseResponse<unknown>>(API_ENDPOINTS.VideoRequests.EditVideoLinks, {
        videoRequestId: requestId,
        videosAndLinks: videoLinks,
      })
      .pipe(take(1));
  }

  addUpdateVideoLinks(requestId: number, videoLinks: IVideoLink[]) {
    return this.#http
      .post<BaseResponse<unknown>>(
        API_ENDPOINTS.VideoRequests.AddUpdateVideoLinks,
        {
          videoRequestId: requestId,
          videosLinks: videoLinks,
        }
      )
      .pipe(take(1));
  }

  addUpdateVideoFiles(requestId: number, videoFiles: IVideoFile[]) {
    return this.#http
      .post<BaseResponse<unknown>>(
        API_ENDPOINTS.VideoRequests.AddUpdateVideoFiles,
        {
          videoRequestId: requestId,
          videoFiles: videoFiles,
        }
      )
      .pipe(take(1));
  }

  startTranscode(request: StartTranscodeRequest) {
    return this.#http
      .post<BaseResponse<StartTranscodeResponse>>(
        API_ENDPOINTS.VideoRequests.StartTranscode,
        request,
        {
          headers: {
            'X-Ignore-Loader': 'true',
            'X-Skip-Messages': 'true',
          },
        }
      )
      .pipe(take(1));
  }

  getTranscodeStatus(jobName: string) {
    const encodedJobName = encodeURIComponent(jobName);
    return this.#http
      .get<BaseResponse<TranscodeStatusResponse>>(
        `${API_ENDPOINTS.VideoRequests.GetTranscodeStatus}/${encodedJobName}`,
        {
          headers: {
            'X-Ignore-Loader': 'true',
            'X-Skip-Messages': 'true',
          },
        }
      )
      .pipe(take(1));
  }

  deleteUploadedVideo(videoId: number, includeMessage?: boolean) {
    let url = `${API_ENDPOINTS.VideoRequests.DeleteVideo}/${videoId}`;
    if (includeMessage === false) {
      url += '?includeMessage=false';
    }
    return this.#http.delete<BaseResponse<unknown>>(url);
  }

  downloadVideo(videoUrl: string, fileName: string) {
    return this.#http.get(videoUrl, { responseType: 'blob' }).pipe(
      take(1),
      tap(binaryData => {
        // transform data to blob and create url
        const file = new Blob([binaryData]);
        const url = window.URL.createObjectURL(file);
        // Create an Anchor element
        const anchorEl = document.createElement('a');
        anchorEl.download = fileName;
        anchorEl.href = url;

        //   dispatch click event on the anchor element
        anchorEl.dispatchEvent(
          new MouseEvent('click', {
            bubbles: true,
            cancelable: true,
            view: window,
          })
        );
        window.URL.revokeObjectURL(url);
      })
    );
  }
}
